import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RankmiTable } from '@rankmi/rankmi-table'
import { faPlus } from '@fortawesome/pro-solid-svg-icons/faPlus'
import { faUser } from '@fortawesome/pro-solid-svg-icons/faUser'
import { rolesService } from 'api/configuration/roles'
import { IRole } from 'interfaces/roles'
import { AvatarWithInfo } from 'components/reusables/avatarWithInfo/AvatarWithInfo'
import { Modal } from 'components/reusables/modal'
import { ModalButtons } from 'components/reusables/modalButtons'
import { RankmiTableWrapper } from './styled'

interface IAssignedUser {
  id: number
  key?: number
  firstName: string
  lastName: string
  avatar: string
  identifier: string
}

interface IProps {
  role: IRole
  close: () => void
}

const Detail = ({ role, close }: IProps) => {
  const { t } = useTranslation()
  const prefix = 'configuration.rolesAndPermissions.users.table'
  const closeLabel =
    'configuration.rolesAndPermissions.roles.table.headers.accessToCourses.courses.detail.table.actions.close'
  const [loading, setLoading] = useState(false)
  const [users, setUsers] = useState<IAssignedUser[]>([])
  const [total, setTotal] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [searchCriteria, setSearchCriteria] = useState('')

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      setLoading(true)
      try {
        const response = await rolesService.getUsersByRole(
          role.id,
          currentPage,
          rowsPerPage,
          searchCriteria
        )
        if (cancelled) return
        const payload = response.data
        setUsers(
          (payload?.users ?? []).map((user) => ({ ...user, key: user.id }))
        )
        setTotal(payload?.total ?? 0)
      } catch {
        if (!cancelled) {
          setUsers([])
          setTotal(0)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [role.id, currentPage, rowsPerPage, searchCriteria])

  const onSearch = (value: string) => {
    setSearchCriteria(value)
    setCurrentPage(1)
  }

  const onPaginationChange = (value: number) => {
    setCurrentPage(value)
  }

  const onRowsPerPageChange = (value: number) => {
    setRowsPerPage(value)
    setCurrentPage(1)
  }

  const locale = {
    header: {
      rowsCount: t(`${prefix}.rowsCountV2`),
      rowsCount_plural: t(`${prefix}.rowsCount_pluralV2`),
    },
  }

  const columns = [
    {
      title: `${t(`${prefix}.headers.name`)}`,
      key: 'name',
      render: (_name: string, user: IAssignedUser) => (
        <AvatarWithInfo
          borderRadius={50}
          icon={faUser}
          image={user.avatar}
          principalColor="#0085FF"
          background="#0085FF"
          size={30}
          title={`${user.firstName} ${user.lastName}`}
          subtitle={user.identifier}
          fontWeight="normal"
        />
      ),
    },
  ]

  return (
    <Modal
      isOpen={true}
      title={t('configuration.rolesAndPermissions.roles.table.headers.users')}
      maxwidth={640}
      maxheight={560}
      close={close}
      actions={
        <ModalButtons secondaryText={t(closeLabel)} secondaryAction={close} />
      }
    >
      <RankmiTableWrapper>
        <RankmiTable
          columnHeaders={columns}
          addButtonIcon={faPlus}
          data={users}
          countIcon={faUser}
          total={total}
          locale={locale}
          loading={loading}
          currentPage={currentPage}
          initialRowsPerPage={rowsPerPage}
          onPaginationChange={onPaginationChange}
          onRowsPerPageChange={onRowsPerPageChange}
          search={searchCriteria}
          onSearch={onSearch}
          delaySearch
        />
      </RankmiTableWrapper>
    </Modal>
  )
}

export default Detail
